import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';

import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import ListItemButton from '@mui/material/ListItemButton';
import useTenantConfig from "@/hooks/useTenantConfig";

import { usePathname } from '@/routes/hooks';
import { RouterLink } from '@/routes/components';

import { useResponsive } from '@/hooks/use-responsive';

import Logo from '@/components/logo';
import Scrollbar from '@/components/scrollbar';

import { NAV } from './config-layout';
import navConfig from './config-navigation';

import {Chip, Divider} from "@mui/material";

// ----------------------------------------------------------------------

const CompanyIcon = name => (
  <Box
    component="img"
    src={`/assets/images/company_avatar/${name}`}
    sx={{
      mx: "auto",
      width: '100%',
      height: '100%',
    }}
  />
);
export default function Nav({ openNav, onCloseNav }) {
  const pathname = usePathname();
  //const company = JSON.parse(getLocalStorage(LS_AUTHCOMPANY));
  const { forceToggleMenu, company, slogan, mail, icon } = useTenantConfig();

  const upLg = useResponsive('up', 'lg');

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderAccount = () => {
    //const userCompany = tenants.find(el => +el.tenantId == +company?.id);

    return (
      <Box
        sx={{
          my: 2,
          mx: 1.5,
          py: 1,
          px: 1,
          display: 'flex',
          borderRadius: 1.5,
          alignItems: 'center',
          bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12),
        }}
      >
        <Box component="span" sx={{ width: 80, height: 64 }}>
          {CompanyIcon(icon || '')}
        </Box>

        <Box sx={{ ml: 1 }}>
          <Typography variant="subtitle2" sx={{fontSize: '0.65rem'}}>{company || '-'}</Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.65rem'}}>
            {slogan || '-'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'gradients.info.main', fontSize: '0.55rem'}}>
            {mail || '-'}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderMenu = (
    <Stack component="nav" spacing={0.5} sx={{ px: 2 }}>
      {navConfig.map(
        (item) => {
          switch(item.type) {
            case 'link':
              return (<NavItem key={item.title} item={item} />)
              break;
            case 'divider':
              return (<Divider key={`${item.text}_key`}
                               orientation={item.orientation}
                               variant={item.variant}
                               textAlign={item.textAlign}><Chip label={item.text} size="small" /></Divider>)
              break;
          }
        }
      )}
    </Stack>
  );

  const renderUpgrade = (
    <Box sx={{ px: 2.5, pb: 3, mt: 10 }}>
      <Stack alignItems="center" spacing={3} sx={{ pt: 5, borderRadius: 2, position: 'relative' }}>
        <Box
          component="img"
          src="/assets/illustrations/illustration_avatar.png"
          sx={{ width: 100, position: 'absolute', top: -50 }}
        />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6">Get more?</Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            From only $69
          </Typography>
        </Box>

        <Button
          href="https://material-ui.com/store/items/minimal-dashboard/"
          target="_blank"
          variant="contained"
          color="inherit"
        >
          Upgrade to Pro
        </Button>
      </Stack>
    </Box>
  );

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Logo sx={{ mt: 3, ml: 4 }} />

      {renderAccount()}

      {renderMenu}

      <Box sx={{ flexGrow: 1 }} />

      {/* renderUpgrade */}

    </Scrollbar>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: (upLg && !forceToggleMenu) ? NAV.WIDTH : 0 },
      }}
    >
      {(upLg && !forceToggleMenu) ? (
        <Box
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.WIDTH,
            borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
          }}
        >
          {renderContent}
        </Box>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.WIDTH,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};

// ----------------------------------------------------------------------

function NavItem({ item }) {
  const pathname = usePathname();

  const active = item.path === pathname;

  return (
    <ListItemButton
      component={RouterLink}
      href={item.path}
      sx={{
        minHeight: 44,
        borderRadius: 0.75,
        typography: 'body2',
        color: 'text.secondary',
        textTransform: 'capitalize',
        fontWeight: 'fontWeightMedium',
        ...(active && {
          color: 'primary.main',
          fontWeight: 'fontWeightSemiBold',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
          },
        }),
      }}
    >
      <Box component="span" sx={{ width: 24, height: 24, mr: 2 }}>
        {item.icon}
      </Box>

      <Box component="span">{item.title}</Box>
    </ListItemButton>
  );
}

NavItem.propTypes = {
  item: PropTypes.object,
};
